import { useEffect, useState } from "react";
import { api, errorText } from "./api";

/* Signup, end to end, on one page.

   The whole product promise is that a vtuber goes from a code in a Discord DM
   to a live instance without talking to anybody. So this page does the three
   things that could go wrong — bad code, taken subdomain, mistyped API key —
   before it commits, and then shows the two passwords once with a warning that
   is deliberately blunt, because there is no email to recover them with. */

interface ClaimResult {
  slug: string;
  display_name: string;
  panel_url: string;
  overlay_url: string;
  password: string;
  admin_password: string;
}

type SlugState = { checking: boolean; available: boolean | null; reason: string | null };

/* What the key check came back with. Unchecked is not the same as bad — the
   button is there to be pressed, not to be a gate. */
type KeyCheck =
  | { state: "idle" }
  | { state: "checking" }
  | { state: "bad"; reason: string }
  | { state: "good"; tier: string | null; remaining: number; limit: number };

export function Claim({ baseDomain, onBack }: { baseDomain: string; onBack?: () => void }) {
  const params = new URLSearchParams(location.search);
  const [code, setCode] = useState(params.get("code") ?? "");
  const [codeOk, setCodeOk] = useState<boolean | null>(null);
  const [slug, setSlug] = useState("");
  const [slugState, setSlugState] = useState<SlugState>({
    checking: false,
    available: null,
    reason: null,
  });
  const [displayName, setDisplayName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [keyCheck, setKeyCheck] = useState<KeyCheck>({ state: "idle" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ClaimResult | null>(null);

  // Check the code as soon as there is one, so somebody who followed a dead
  // link finds out before filling in the whole form.
  useEffect(() => {
    const trimmed = code.trim();
    if (!trimmed) {
      setCodeOk(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const status = await api<{ usable: boolean }>(
          `/api/claim/code/${encodeURIComponent(trimmed)}`,
        );
        if (!cancelled) setCodeOk(status.usable);
      } catch {
        if (!cancelled) setCodeOk(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [code]);

  // Same for the subdomain, debounced so every keystroke is not a request.
  useEffect(() => {
    const trimmed = slug.trim().toLowerCase();
    if (!trimmed) {
      setSlugState({ checking: false, available: null, reason: null });
      return;
    }
    let cancelled = false;
    setSlugState((prior) => ({ ...prior, checking: true }));
    const timer = setTimeout(async () => {
      try {
        const status = await api<{ available: boolean; reason: string | null }>(
          `/api/claim/slug/${encodeURIComponent(trimmed)}`,
        );
        if (!cancelled) {
          setSlugState({ checking: false, available: status.available, reason: status.reason });
        }
      } catch {
        if (!cancelled) setSlugState({ checking: false, available: null, reason: null });
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [slug]);

  const checkKey = async () => {
    setKeyCheck({ state: "checking" });
    try {
      const body = await api<{
        subscription: { tier: string | null; remaining: number; limit: number };
      }>("/api/claim/test-key", {
        method: "POST",
        body: JSON.stringify({ code: code.trim(), api_key: apiKey.trim() }),
      });
      setKeyCheck({
        state: "good",
        tier: body.subscription.tier,
        remaining: body.subscription.remaining,
        limit: body.subscription.limit,
      });
    } catch (caught) {
      setKeyCheck({ state: "bad", reason: errorText(caught) });
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setResult(
        await api<ClaimResult>("/api/claim", {
          method: "POST",
          body: JSON.stringify({
            code: code.trim(),
            slug: slug.trim().toLowerCase(),
            display_name: displayName.trim(),
            api_key: apiKey.trim(),
          }),
        }),
      );
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  if (result) return <Done result={result} />;

  const ready =
    codeOk === true && slugState.available === true && displayName.trim() && apiKey.trim();

  return (
    <main className="claim">
      {onBack && (
        <button className="back-link" onClick={onBack}>
          &larr; ToonSpeech
        </button>
      )}
      <h1>Set up your instance</h1>


      <form onSubmit={submit} className="claim-form">
        <label>
          Invite code
          <input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="abcd-efgh-ijkl-mnop"
            autoComplete="off"
          />
          {code.trim() && codeOk === false && (
            <small className="bad">Invalid or already used.</small>
          )}
          {codeOk === true && <small className="good">Code accepted.</small>}
        </label>

        <label>
          Subdomain
          <span className="slug-row">
            <input
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              placeholder="yourname"
              autoComplete="off"
              spellCheck={false}
            />
            <span className="suffix">.{baseDomain}</span>
          </span>
          {slugState.checking && <small className="muted">Checking…</small>}
          {!slugState.checking && slugState.available === true && (
            <small className="good">
              {slug.trim().toLowerCase()}.{baseDomain} is free.
            </small>
          )}
          {!slugState.checking && slugState.available === false && (
            <small className="bad">{slugState.reason ?? "Not available."}</small>
          )}
        </label>

        <label>
          Display name
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Your channel name"
            maxLength={60}
          />
        </label>

        {/* The field people abandon this form on: a long secret pasted from
            another site, with no feedback until the whole signup failed. The
            button turns it into the one step that reassures. */}
        <label>
          ElevenLabs API key
          <span className="field-row">
            <input
              type="password"
              value={apiKey}
              onChange={(event) => {
                setApiKey(event.target.value);
                // Whatever the last check said was about a different string.
                setKeyCheck({ state: "idle" });
              }}
              placeholder="sk_…"
              autoComplete="off"
            />
            <button
              type="button"
              className="ghost"
              disabled={
                !apiKey.trim() || codeOk !== true || keyCheck.state === "checking"
              }
              onClick={() => void checkKey()}
            >
              {keyCheck.state === "checking" ? "Checking…" : "Check key"}
            </button>
          </span>

          {keyCheck.state === "good" && (
            <small className="good">
              Key works. {keyCheck.remaining.toLocaleString()}
              {keyCheck.limit ? ` / ${keyCheck.limit.toLocaleString()}` : ""} characters left.
            </small>
          )}
          {keyCheck.state === "bad" && <small className="bad">{keyCheck.reason}</small>}

          <small className="muted">
            Get one at{" "}
            <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noreferrer">
              elevenlabs.io
            </a>
            .
          </small>
        </label>

        {error && <p className="bad">{error}</p>}

        <button className="primary" type="submit" disabled={!ready || busy}>
          {busy ? "Setting it up…" : "Create my instance"}
        </button>
      </form>
    </main>
  );
}

/* Everything on the shown-once screen, as one block of text.

   Four separate Copy buttons is four chances to close the tab having taken
   three of them, on a screen there is deliberately no way back to. */
function secretsText(result: ClaimResult): string {
  return [
    `${result.display_name} (ToonSpeech)`,
    ``,
    `Panel:            ${result.panel_url}`,
    `Password:         ${result.password}`,
    `Admin password:   ${result.admin_password}`,
    `OBS browser source URL:`,
    `  ${result.overlay_url}`,
    ``,
    `Keep the overlay URL private.`,
  ].join("\n");
}

function Done({ result }: { result: ClaimResult }) {
  const [copiedAll, setCopiedAll] = useState(false);

  function download() {
    // A file survives a closed tab and a clipboard that has moved on, which is
    // the failure this screen actually has.
    const blob = new Blob([secretsText(result)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `toonspeech-${result.slug}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="claim">
      <h1>{result.display_name} is live</h1>

      <div className="warn-box">
        <strong>Save these now.</strong> You won't see the passwords again.
        <div className="warn-actions">
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(secretsText(result));
                setCopiedAll(true);
                setTimeout(() => setCopiedAll(false), 2000);
              } catch {
                setCopiedAll(false);
              }
            }}
          >
            {copiedAll ? "Copied everything" : "Copy everything"}
          </button>
          <button className="ghost" onClick={download}>
            Download
          </button>
        </div>
      </div>

      <Secret label="Panel" value={result.panel_url} link />
      <Secret label="Password" value={result.password} />
      <Secret label="Admin password" value={result.admin_password} />
      <Secret label="OBS browser source URL" value={result.overlay_url} />

      <h2>Next</h2>
      <ol className="next-steps">
        <li>Open your panel and sign in.</li>
        <li>
          In OBS, add a <strong>Browser</strong> source with the overlay URL at
          1920×1080.
        </li>
        <li>Give the normal password to chat. Keep the admin one.</li>
      </ol>
    </main>
  );
}

function Secret({ label, value, link }: { label: string; value: string; link?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="secret">
      <span className="secret-label">{label}</span>
      <code>{value}</code>
      <button
        className="ghost"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* Refused on an insecure origin or a locked-down browser. The value
               is on screen and selectable, so this is not a dead end. */
          }
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
      {link && (
        <a className="ghost" href={value} target="_blank" rel="noreferrer">
          Open
        </a>
      )}
    </div>
  );
}
